import type { Selection } from '../state';

export type LabelStyle =
  | 'star' | 'planet' | 'moon' | 'sun' | 'constellation' | 'dso' | 'satellite' | 'iss' | 'radiant' | 'cardinal' | 'cardinalN' | 'selected'
  | 'arabic' | 'arabicStar' | 'station' | 'culture' | 'cultureStar';

export interface LabelCandidate {
  x: number; // CSS px, anchor (object centre)
  y: number;
  r: number; // object radius in CSS px (label sits to the right of it; centred labels with a radius sit below it)
  text: string;
  sub?: string;
  style: LabelStyle;
  priority: number; // higher first
  sel?: Selection;
  centered?: boolean;
  dim?: boolean; // unlit, e.g. a lunar feature on the night side
  below?: boolean; // below the horizon: fainter, in italics
  counted?: boolean; // one of the names the budget limits (LabelOptions.budget)
}

const FAMILY = '-apple-system, BlinkMacSystemFont, system-ui, "Segoe UI", Roboto, sans-serif';
/** Arabic script first, with the Latin fallbacks for transliterations and numbers. */
export const ARABIC_FAMILY = '"Adwa as-Salaf", "SF Arabic", "Geeza Pro", "Noto Naskh Arabic", "Noto Sans Arabic", "Segoe UI", Tahoma, sans-serif';

type RGBA = readonly [number, number, number, number];
interface Style { font: string; light: RGBA; dark: RGBA; upper?: boolean; spacing?: number; family?: string }

// Light text for a dark sky; dark text (a deeper shade of the same hue) where the sky behind is bright.
const NAVY: RGBA = [10, 24, 52, 1];
const AMBER: RGBA = [58, 34, 3, 1];
const TEAL: RGBA = [0, 42, 56, 1];
const PINE: RGBA = [8, 44, 35, 1];
const STYLES: Record<LabelStyle, Style> = {
  star: { font: '500 12.5px', light: [226, 232, 246, 0.88], dark: NAVY },
  planet: { font: '600 13px', light: [255, 213, 150, 1], dark: AMBER },
  moon: { font: '600 13px', light: [250, 244, 230, 0.96], dark: [52, 38, 16, 1] },
  sun: { font: '600 13px', light: [255, 214, 122, 1], dark: AMBER },
  constellation: { font: '500 11px', light: [176, 190, 224, 0.75], dark: NAVY, upper: true, spacing: 2 },
  dso: { font: '500 11px', light: [158, 217, 198, 1], dark: PINE },
  satellite: { font: '500 11.5px', light: [127, 227, 255, 1], dark: TEAL },
  iss: { font: '600 12.5px', light: [127, 227, 255, 1], dark: TEAL },
  radiant: { font: '600 11.5px', light: [200, 168, 255, 1], dark: [46, 22, 92, 1] },
  cardinal: { font: '600 12px', light: [222, 228, 245, 0.86], dark: NAVY },
  cardinalN: { font: '700 13px', light: [255, 128, 110, 0.98], dark: [84, 16, 8, 1] },
  selected: { font: '700 13px', light: [255, 255, 255, 1], dark: NAVY },
  arabic: { font: '500 15px', light: [255, 212, 150, 0.8], dark: AMBER, family: ARABIC_FAMILY },
  arabicStar: { font: '500 13px', light: [255, 226, 186, 0.9], dark: AMBER, family: ARABIC_FAMILY },
  station: { font: '500 13px', light: [120, 230, 190, 0.82], dark: PINE, family: ARABIC_FAMILY },
  culture: { font: '500 12.5px', light: [150, 232, 210, 0.82], dark: PINE },
  cultureStar: { font: '500 12px', light: [206, 242, 230, 0.88], dark: PINE },
};
const SUB_FONT = '500 10.5px';

const lin = (v: number) => {
  const c = v / 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
};
/** WCAG luminance of a colour drawn over black. */
const lumOf = (c: RGBA) => (0.2126 * lin(c[0] * c[3]) + 0.7152 * lin(c[1] * c[3]) + 0.0722 * lin(c[2] * c[3]));

/**
 * One line's colours. On a brighter sky (twilight, moonlight, city glow) light text moves towards a near-white,
 * opaque version of itself just far enough to keep about 5.5:1; `ln`, `lg` and `ld` are the luminances of the
 * light, near-white and dark ends.
 */
interface Look { light: RGBA; glow: RGBA; dark: RGBA; ln: number; lg: number; ld: number }
function look(light: RGBA, dark: RGBA): Look {
  const glow: RGBA = [light[0] + (255 - light[0]) * 0.9, light[1] + (255 - light[1]) * 0.9, light[2] + (255 - light[2]) * 0.9, 1];
  return { light, glow, dark, ln: lumOf(light), lg: lumOf(glow), ld: lumOf(dark) };
}

/** The contrast plates aim for: WCAG AA (4.5:1) for small text, with a margin for sky between the sample points. */
const TARGET = 5;
/** Plates never hide the sky behind them entirely. */
const PLATE_MAX = 0.75;

/**
 * Where no text colour is enough on its own (a mid-bright dusk sky, a label across a gradient), a soft plate
 * behind the words: dark under light text, light under dark text. How opaque each would have to be, from the
 * brightest (`hi`) and darkest (`lo`) sky behind the line; 0 where the text needs none.
 */
export function plateFor(l: Pick<Look, 'lg' | 'ld'>, lo: number, hi: number): { light: number; dark: number } {
  const maxBg = (l.lg + 0.05) / TARGET - 0.05; // the brightest sky near-white text can stand
  const minBg = TARGET * (l.ld + 0.05) - 0.05; // the darkest sky dark text can stand
  return {
    light: hi > maxBg ? Math.min(PLATE_MAX, 1 - maxBg / hi) : 0,
    dark: lo < minBg ? Math.min(PLATE_MAX, (minBg - lo) / (1 - lo)) : 0,
  };
}
// Second lines keep at least 0.7 of the name's alpha on a dark sky, and all of it on a bright one.
const LOOKS = Object.fromEntries(
  Object.entries(STYLES).map(([k, s]) => [k, { main: look(s.light, s.dark), sub: look([s.light[0], s.light[1], s.light[2], s.light[3] * 0.72], s.dark) }]),
) as Record<LabelStyle, { main: Look; sub: Look }>;

const rgba = (c: ArrayLike<number>) => `rgba(${Math.round(c[0])},${Math.round(c[1])},${Math.round(c[2])},${+c[3].toFixed(3)})`;
function lightColour(l: Look, bright: number): string {
  if (l.lg <= l.ln) return rgba(l.light);
  const t = Math.min(1, Math.max(0, (5.5 * (bright + 0.05) - 0.05 - l.ln) / (l.lg - l.ln)));
  const c = l.light, g = l.glow;
  return rgba([c[0] + (g[0] - c[0]) * t, c[1] + (g[1] - c[1]) * t, c[2] + (g[2] - c[2]) * t, c[3] + (g[3] - c[3]) * t]);
}

// Dark text once the darkest sky behind a label is this bright (WCAG luminance), light again below
// DARK_OFF: the gap keeps labels from flickering at the threshold. Near 0.2 both choices give about 3.8:1
// on their own; the halo carries the rest.
const DARK_ON = 0.22;
const DARK_OFF = 0.18;
const FADE = 0.25; // s, cross-fade when a label changes over
const tones = new Map<string, { dark: boolean; k: number; seen: number }>();

/**
 * 0 for light-on-dark, 1 for dark-on-bright (in between while fading), for something drawn over the sky
 * whose darkest background luminance is `lum`. `key` names it between frames; `time` is in seconds.
 */
export function tone(key: string, lum: number, time: number, instant = false): number {
  return settle(key, lum > DARK_ON, lum < DARK_OFF, time, instant);
}

/**
 * As tone(), for a line of a label: dark text where it needs a lighter plate than light text would, light text
 * otherwise, with a margin either way so a label does not flicker between the two.
 */
export function toneByPlate(key: string, p: { light: number; dark: number }, time: number, instant = false): number {
  const d = p.light - p.dark;
  return settle(key, d > 0.04, d < -0.04, time, instant);
}

/** Cross-fade state: turn dark when `on`, back to light when `off`. */
function settle(key: string, on: boolean, off: boolean, time: number, instant: boolean): number {
  let s = tones.get(key);
  if (!s || time - s.seen > 1 || time < s.seen) {
    s = { dark: on, k: on ? 1 : 0, seen: time };
    tones.set(key, s);
    if (tones.size > 600) for (const [n, v] of tones) if (time - v.seen > 5) tones.delete(n);
    return s.k;
  }
  if (s.dark ? off : on) s.dark = !s.dark;
  const step = instant ? 1 : (time - s.seen) / FADE;
  s.k = s.dark ? Math.min(1, s.k + step) : Math.max(0, s.k - step);
  s.seen = time;
  return s.k;
}

/**
 * A plate `a` opaque at its heart and feathered at its edges, behind a line of text `w` wide centred on `y`
 * (CSS px). Drawn as the blurred shadow of a shape far off to the side, so only the soft part shows.
 */
function plate(ctx: CanvasRenderingContext2D, rgb: string, a: number, x: number, y: number, w: number, h: number, dpr: number, sc: number) {
  if (a < 0.02) return;
  const padX = 7 * sc, padY = 4 * sc, far = 10000;
  ctx.save();
  ctx.shadowColor = `rgba(${rgb},${a.toFixed(3)})`;
  ctx.shadowBlur = 6 * sc * dpr;
  ctx.shadowOffsetX = far * dpr; // shadow offsets ignore the transform: device pixels
  ctx.fillStyle = '#000';
  ctx.beginPath();
  const r = h / 2 + padY;
  if (ctx.roundRect) ctx.roundRect(x - padX - far, y - h / 2 - padY, w + 2 * padX, h + 2 * padY, r);
  else ctx.rect(x - padX - far, y - h / 2 - padY, w + 2 * padX, h + 2 * padY);
  ctx.fill();
  ctx.restore();
}

/** Halo behind light text (drawn twice, so it is solid) and behind dark text (one soft pass); blur in CSS px. */
const HALO_DARK = { color: 'rgba(3,5,10,0.85)', base: 'rgb(3,5,10)', blur: 2 };
const HALO_LIGHT = { color: 'rgba(255,255,255,0.35)', blur: 4 / 3 };

/** Canvas shadows ignore the transform, hence the device-pixel blur. */
function halo(ctx: CanvasRenderingContext2D, dark: boolean, dpr: number) {
  const h = dark ? HALO_LIGHT : HALO_DARK;
  ctx.shadowColor = h.color;
  ctx.shadowBlur = h.blur * dpr;
}

function fillLine(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, color: string, dark: boolean, dpr: number) {
  halo(ctx, dark, dpr);
  if (!dark) {
    // The first pass lays the halo and an opaque base; the second draws the text at its own alpha with the halo again.
    ctx.fillStyle = HALO_DARK.base;
    ctx.fillText(text, x, y);
  }
  ctx.fillStyle = color;
  ctx.fillText(text, x, y);
}

/** A small tag drawn like a label (an hour on a path, with its dot): `k` from tone(), 0 light … 1 dark. */
export function drawTag(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, dot: { x: number; y: number }, k: number, colors: { light: string; dark: string }, dpr: number) {
  for (const dark of [false, true]) {
    const a = dark ? k : 1 - k;
    if (a <= 0.01) continue;
    ctx.globalAlpha = a;
    halo(ctx, dark, dpr);
    ctx.fillStyle = dark ? colors.dark : colors.light;
    ctx.beginPath();
    ctx.arc(dot.x, dot.y, 2, 0, Math.PI * 2);
    ctx.fill();
    fillLine(ctx, text, x, y, dark ? colors.dark : colors.light, dark, dpr);
  }
  ctx.shadowBlur = 0;
  ctx.globalAlpha = 1;
}

interface Rect { x0: number; y0: number; x1: number; y1: number }
export interface LabelHit extends Rect { sel: Selection }

/**
 * Darkest and brightest sky behind a box: a 5 × 4 grid over its full height, then, where that finds sky and
 * ground or a steep gradient, every 3 px across (the trees on the skyline are narrower than the grid).
 */
function probe(skyLum: (x: number, y: number) => number, b: Rect): [number, number] {
  let lo = 1, hi = 0;
  const take = (x: number, y: number) => {
    const v = skyLum(x, y);
    if (v < lo) lo = v;
    if (v > hi) hi = v;
  };
  for (let i = 0; i < 5; i++) for (let j = 0; j < 4; j++) take(b.x0 + 3 + ((b.x1 - b.x0 - 6) * i) / 4, b.y0 + 1 + ((b.y1 - b.y0 - 2) * j) / 3);
  if (hi - lo > 0.12) for (let x = b.x0 + 2; x <= b.x1 - 2; x += 3) for (let j = 0; j < 3; j++) take(x, b.y0 + 2 + ((b.y1 - b.y0 - 4) * j) / 2);
  return [lo, hi];
}

export interface LabelOptions {
  /** WCAG luminance (0–1) of the sky drawn at a screen point (CSS px); without it the sky counts as dark. */
  skyLum?: (x: number, y: number) => number;
  /** Area labels must stay inside (the safe area), CSS px. */
  bounds?: Rect;
  /** Canvas pixels per CSS px (shadow blur is not scaled by the transform). */
  dpr?: number;
  /** Seconds, for the light/dark cross-fade. */
  time?: number;
  /** Reduced motion: change over at once. */
  still?: boolean;
  /** Text size, following the reader's setting (1 = as designed). */
  scale?: number;
  /** How many `counted` labels may be placed (Naked eye's star names): once that many are, the rest are left out. */
  budget?: number;
  /** Points of light (Naked eye's stars) to keep in sight: `counted` labels never cover one, others only if they must. */
  dots?: Rect[];
}

/** Greedy label placement: highest priority first, skip anything that would overlap. Returns tappable boxes. */
export function drawLabels(ctx: CanvasRenderingContext2D, labels: LabelCandidate[], w: number, h: number, reserved: Rect[] = [], o: LabelOptions = {}): LabelHit[] {
  labels.sort((a, b) => b.priority - a.priority);
  const placed: Rect[] = [...reserved];
  const hits: LabelHit[] = [];
  const B = o.bounds ?? { x0: 2, y0: 2, x1: w - 2, y1: h - 2 };
  const dpr = o.dpr ?? 1, time = o.time ?? 0, sc = o.scale ?? 1;
  const ls = ctx as unknown as { letterSpacing?: string }; // widely supported; ignored where not
  const sized = (f: string) => f.replace(/([\d.]+)px/, (_, v: string) => `${+(parseFloat(v) * sc).toFixed(2)}px`);
  ctx.textBaseline = 'middle';
  let counted = 0;
  for (const L of labels) {
    if (L.counted && o.budget !== undefined && counted >= o.budget) continue;
    const f = STYLES[L.style];
    const text = f.upper ? L.text.toUpperCase() : L.text;
    const slant = L.below ? 'italic ' : '';
    const font = `${slant}${sized(f.font)} ${f.family ?? FAMILY}`, subFont = `${slant}${sized(SUB_FONT)} ${FAMILY}`;
    const spacing = `${(f.spacing ?? 0) * sc}px`;
    ctx.font = font;
    ls.letterSpacing = spacing;
    const tw = ctx.measureText(text).width;
    let subW = 0;
    if (L.sub) {
      ctx.font = subFont;
      ls.letterSpacing = '0px';
      subW = ctx.measureText(L.sub).width;
    }
    const width = Math.max(tw, subW);
    const lineH = ((L.sub ? 26 : 14) + (f.family ? 3 : 0)) * sc;
    // Candidate positions: right, left, above, below (centred: on the anchor, or below or above its radius); for the
    // names a budget counts (Naked eye's stars, where a crowded sky needs room for every one), the corners after those.
    const gap = L.r + 5 * sc, k = gap * 0.7;
    const opts: [number, number][] = L.centered
      ? L.r > 0
        ? [[L.x - width / 2, L.y + gap], [L.x - width / 2, L.y - gap - lineH]]
        : [[L.x - width / 2, L.y - lineH / 2]]
      : [
          [L.x + gap, L.y - lineH / 2],
          [L.x - gap - width, L.y - lineH / 2],
          [L.x - width / 2, L.y - gap - lineH],
          [L.x - width / 2, L.y + gap],
          ...(L.counted ? ([[L.x + k, L.y - k - lineH], [L.x + k, L.y + k], [L.x - k - width, L.y - k - lineH], [L.x - k - width, L.y + k]] as [number, number][]) : []),
        ];
    // The sky behind each line: its darkest and brightest points. Take the first free place unless a line
    // there straddles bright sky and dark (the skyline, say), where either colour would need a heavy plate,
    // or covers a star that is to stay in sight; then the next free place that does neither, if there is one.
    const lk = LOOKS[L.style];
    const hard = ([lo, hi]: [number, number], i: number) => {
      const p = plateFor(i ? lk.sub : lk.main, lo, hi);
      return Math.min(p.light, p.dark) > 0.2;
    };
    let box: Rect | null = null, sky: [number, number][] = [], worst = 4;
    for (const [x, y] of opts) {
      const r = { x0: x - 2, y0: y - 1, x1: x + width + 2, y1: y + lineH + 1 };
      if (r.x0 < B.x0 || r.y0 < B.y0 || r.x1 > B.x1 || r.y1 > B.y1) continue;
      if (placed.some((p) => r.x0 < p.x1 && r.x1 > p.x0 && r.y0 < p.y1 && r.y1 > p.y0)) continue;
      const onDot = !!o.dots && o.dots.some((p) => r.x0 < p.x1 && r.x1 > p.x0 && r.y0 < p.y1 && r.y1 > p.y0);
      if (onDot && L.counted) continue;
      const mid = (r.y0 + r.y1) / 2;
      const s: [number, number][] = !o.skyLum ? [[0, 0]] : L.sub ? [probe(o.skyLum, { ...r, y1: mid }), probe(o.skyLum, { ...r, y0: mid })] : [probe(o.skyLum, r)];
      const bad = (s.some(hard) ? 1 : 0) + (onDot ? 2 : 0);
      if (bad < worst) [box, sky, worst] = [r, s, bad];
      if (!bad) break;
    }
    if (!box) continue;
    if (L.counted) counted++;
    placed.push(box);
    if (L.sel) hits.push({ ...box, sel: L.sel });

    // Light or dark text, whichever the sky behind the line suits better; light text brightens for the
    // brightest part, and a soft plate makes up whatever contrast is still missing.
    const x = box.x0 + 2, ty = box.y0 + 1 + (f.family ? 8.5 : 7) * sc;
    const size = parseFloat(sized(f.font).split(' ')[1]), subSize = 10.5 * sc;
    const lines = [{ t: text, x: x + (L.centered ? (width - tw) / 2 : 0), y: ty, w: tw, h: size, font, spacing, look: lk.main, sky: sky[0], key: `${L.style}|${L.text}` }];
    if (L.sub) lines.push({ t: L.sub, x: x + (L.centered ? (width - subW) / 2 : 0), y: ty + 13 * sc, w: subW, h: subSize, font: subFont, spacing: '0px', look: lk.sub, sky: sky[1] ?? sky[0], key: `${L.style}|${L.text}|2` });
    const alpha = L.below || L.dim ? 0.7 : 1;
    for (const ln of lines) {
      const need = o.skyLum ? plateFor(ln.look, ln.sky[0], ln.sky[1]) : { light: 0, dark: 0 };
      const k = toneByPlate(ln.key, need, time, o.still);
      ctx.globalAlpha = alpha;
      plate(ctx, '3,5,10', need.light * (1 - k), ln.x, ln.y, ln.w, ln.h, dpr, sc);
      plate(ctx, '255,255,255', need.dark * k, ln.x, ln.y, ln.w, ln.h, dpr, sc);
      ctx.font = ln.font;
      ls.letterSpacing = ln.spacing;
      for (const dark of [false, true]) {
        const a = alpha * (dark ? k : 1 - k);
        if (a <= 0.01) continue;
        ctx.globalAlpha = a;
        fillLine(ctx, ln.t, ln.x, ln.y, dark ? rgba(ln.look.dark) : lightColour(ln.look, ln.sky[1]), dark, dpr);
      }
    }
    ctx.shadowBlur = 0;
    ctx.globalAlpha = 1;
  }
  ls.letterSpacing = '0px';
  return hits;
}

export { FAMILY };
